// Pro features configuration for different components
export const PRO_FEATURES = {
  notesGenerator: {
    unlimitedPrompts: true,
    exportOptions: true,
    largeFileSupport: true,
    extendedProcessingTime: true,
    fasterProcessing: true,
    customTemplates: true
  },
  aiPracticeQuestions: {
    unlimitedQuestions: true,
    advancedFeedback: true,
    customPrompts: true,
    exportOptions: true,
    statistics: true
  },
  flashcards: {
    unlimitedCards: true,
    advancedSpacing: true,
    customThemes: true,
    exportOptions: true,
    statistics: true
  },
  mindMap: {
    unlimitedMaps: true,
    advancedThemes: true,
    customLayouts: true,
    exportOptions: true,
    collaboration: true
  },
  diagramGenerator: {
    unlimitedDiagrams: true,
    customThemes: true,
    exportOptions: true,
    advancedShapes: true,
    templates: true
  },
  scheduleMaker: {
    unlimitedSchedules: true,
    advancedRecurring: true,
    exportOptions: true,
    reminders: true,
    statistics: true
  },
  samplePaper: {
    unlimitedPapers: true,
    customTemplates: true,
    exportOptions: true,
    solutions: true,
    statistics: true
  }
};

// Pro feature limits
export const PRO_LIMITS = {
  notesGenerator: {
    free: {
      prompts: 5,
      fileSize: 10 * 1024 * 1024, // 10MB
      processingTime: 30000 // 30 seconds
    },
    pro: {
      prompts: 50,
      fileSize: 500 * 1024 * 1024, // 500MB
      processingTime: 500000 // 500 seconds
    }
  },
  aiPracticeQuestions: {
    free: {
      questions: 5,
      topics: 3
    },
    pro: {
      questions: 20,
      topics: 'unlimited'
    }
  },
  flashcards: {
    free: {
      cards: 20,
      themes: 1
    },
    pro: {
      cards: 'unlimited',
      themes: 'unlimited'
    }
  },
  mindMap: {
    free: {
      maps: 5,
      themes: 1
    },
    pro: {
      maps: 'unlimited',
      themes: 'unlimited'
    }
  },
  diagramGenerator: {
    free: {
      diagrams: 5,
      shapes: 10
    },
    pro: {
      diagrams: 'unlimited',
      shapes: 'unlimited'
    }
  },
  scheduleMaker: {
    free: {
      schedules: 3,
      recurring: false
    },
    pro: {
      schedules: 'unlimited',
      recurring: true
    }
  },
  samplePaper: {
    free: {
      papers: 5,
      templates: 1
    },
    pro: {
      papers: 'unlimited',
      templates: 'unlimited'
    }
  }
};

// Initialize pro features for a component
export const initializeProFeatures = (component) => {
  const isPro = localStorage.getItem('isPro') === 'true';
  const features = PRO_FEATURES[component] || {};
  const limits = PRO_LIMITS[component] || {};
  
  // Initialize all features to false for free users
  const featureState = {};
  Object.keys(features).forEach(feature => {
    featureState[feature] = isPro;
  });

  // Log the pro status for debugging
  console.log(`Pro features initialized for ${component}:`, {
    isPro,
    features: featureState,
    limits: isPro ? limits.pro : limits.free
  });

  return {
    isPro,
    features: featureState,
    limits: isPro ? limits.pro : limits.free
  };
};

// Check if a feature is available for the current user
export const isFeatureAvailable = (feature, component) => {
  const isPro = localStorage.getItem('isPro') === 'true';
  if (isPro) return true;
  return PRO_FEATURES[component]?.[feature] || false;
};

// Get limits for a component
export const getLimits = (component) => {
  const isPro = localStorage.getItem('isPro') === 'true';
  return isPro ? PRO_LIMITS[component].pro : PRO_LIMITS[component].free;
};

// Check if user has reached a limit
export const checkLimit = (component, feature, currentValue) => {
  const limits = getLimits(component);
  const limit = limits[feature];
  if (limit === 'unlimited') return false;
  return currentValue >= limit;
};

// Verify pro pin
export const verifyProPin = (pin) => {
  // Use the same PIN across all environments
  return pin === '7287';
};

// Activate pro features
export const activateProFeatures = () => {
  localStorage.setItem('isPro', 'true');
  console.log('Pro features activated');
};

// Deactivate pro features
export const deactivateProFeatures = () => {
  localStorage.setItem('isPro', 'false');
  console.log('Pro features deactivated');
}; 